import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Card, createTheme, IconButton, TextField, ThemeProvider, useTheme } from '@mui/material';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import React, { useEffect, useMemo, useState } from 'react';
import { Invoice, ShipmentHistoryItem } from '../types';
import { standardDateFormat } from '../../../../helpers/date';
import { useAppDispatch } from '../../../../redux/store';
import { getShipmentHistory, getTrackTrace } from '../shipmentService';
import { addError } from '../../../../redux/alerts';
import dayjs from 'dayjs';
import TrackAndTraceModal from '../tracktrace/TrackTrackModal';
import useCustomerNumber from '../../../../hooks/useCustomerNumber';
import CurrencyFormatter from '../../../../components/CurrencyFormatter'

const ShipmentHistory = () => {
  const customerNumber = useCustomerNumber()
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [loadAllYears, setLoadAllYears] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [shipments, setShipments] = useState<ShipmentHistoryItem[]>([]);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [tntId, setTntId] = useState<string | null>(null);

  const tableTheme = createTheme({
    typography: {
      fontFamily: '"Open Sans", sans-serif',
      fontSize: 13
    }
  });

  const filteredShipments = useMemo(() => {
    if (!search) {
      return shipments;
    }

    return shipments.filter(({ invoice_number, tracking_numbers }) => {
      const lowerSearch = search.toLowerCase();
      return invoice_number.toLowerCase().includes(lowerSearch) || tracking_numbers?.toLowerCase().includes(lowerSearch);
    });
  }, [search, shipments]);

  const closeModal = () => {
    setOpenModal(false);
    setInvoice(null);
  };

  const loadRecentShipmentHistory = async () => {
    setLoading(true);
    const endDate = `gte=01/01/${new Date().getFullYear().toString().slice(-2)}`;

    try {
      const shipments = await getShipmentHistory(customerNumber, endDate);
      setShipments(shipments);
    } catch (error: any) {
      console.error(error);
      dispatch(addError(error instanceof Error ? error.message : 'Failed to load shipment history'));
    }

    setLoading(false);
  };

  const loadOldShipmentHistory = async () => {
    setLoading(true);
    const endDate = `lte=01/01/${new Date().getFullYear().toString().slice(-2)}`;

    try {
      const shipments = await getShipmentHistory(customerNumber, endDate);
      setShipments((state) => [...state, ...shipments]);
      setLoadAllYears(true);
    } catch (error: any) {
      console.error(error);
      dispatch(addError(error instanceof Error ? error.message : 'Failed to load shipment history'));
    }

    setLoading(false);
  };

  const shippingDateMax = (trackingDate: string) => {
    const threeMonthsAgo = dayjs().subtract(3, 'month');
    return dayjs(trackingDate) < threeMonthsAgo;
  };

  const getTrackAndTraceInvoice = async (invoiceNumber: string) => {
    setTntId(invoiceNumber);
    if (!invoiceNumber) {
      dispatch(addError('The invoice number is missing'));
      setTntId(null);
      return;
    }

    let invoiceData;

    try {
      invoiceData = await getTrackTrace(invoiceNumber);
    } catch (error) {
      console.error(error);
      dispatch(addError(error instanceof Error ? error.message : 'Cannot get tracking, please try again later'));
      setTntId(null);
      return;
    }

    if (invoiceData) {
      setInvoice(invoiceData);
      setOpenModal(true);
    }
    setTntId(null);
  };

  useEffect(() => {
    if (customerNumber) {
      loadRecentShipmentHistory();
    }
  }, [customerNumber]);

  return (
    <Box sx={{ p: 2, color: theme.palette.color50555b.main }}>
      <div className="f-s-23">Shipment History</div>
      <Card sx={{ width: '100%', mt: 2 }} variant="outlined">
        <div className="flex flex-end p-16">
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search"
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <FontAwesomeIcon icon="magnifying-glass" size="sm" className="m-l-8" />
                </IconButton>
              )
            }}
          />
        </div>
        <ThemeProvider theme={tableTheme}>
          <DataGrid
            sx={{
              borderLeft: 'none',
              borderRight: 'none',
              borderBottom: 'none',
              borderRadius: '0',
              [`& .${gridClasses.cell}`]: {
                py: 1
              }
            }}
            rows={filteredShipments.map((shipment) => ({
              ...shipment,
              id: `${shipment.shipment_date}${Math.floor(Math.random() * (9999 - 1000 + 1) + 1000)}`
            }))}
            columns={[
              {
                field: 'shipment_date',
                headerName: 'Shipment Date',
                width: 250,
                flex: 1,
                renderCell: (params) => standardDateFormat(params.row.shipment_date)
              },
              {
                field: 'invoice_number',
                headerName: 'Invoice Number',
                width: 100,
                flex: 1
              },
              {
                field: 'is_dscsa',
                headerName: 'Track and Trace',
                width: 150,
                flex: 1,
                renderCell: (params) => {
                  return params.row.is_dscsa ? (
                    <Button
                      onClick={() => getTrackAndTraceInvoice(params.row.invoice_number)}
                      sx={{ textTransform: 'none', color: theme.palette.linkColor.main }}
                      disabled={tntId === params.row.invoice_number}
                    >
                      Track and Trace Report
                    </Button>
                  ) : (
                    ''
                  );
                }
              },
              {
                field: 'total',
                headerName: 'Total Amount',
                width: 100,
                flex: 1,
                renderCell: (params) => <CurrencyFormatter value={params.row.total} />
              },
              {
                field: 'tracking_numbers',
                headerName: 'Tracking Numbers',
                width: 250,
                flex: 1,
                renderCell: (params) => {
                  return params.row?.tracking_numbers ? (
                    <div style={{ whiteSpace: 'normal', lineHeight: '1.5', wordBreak: 'break-word' }}>
                      {params.row.tracking_numbers.split(',').map((trackingNumber, index) => {
                        return shippingDateMax(params.row.shipment_date) ? (
                          <Box sx={{ pl: '8px' }} key={index}>
                            {trackingNumber}
                          </Box>
                        ) : (
                          <Button
                            href={`https://www.ups.com/track?tracknum=${trackingNumber}`}
                            target="_blank"
                            key={index}
                            sx={{ color: theme.palette.linkColor.main }}
                          >
                            {trackingNumber}
                          </Button>
                        );
                      })}
                    </div>
                  ) : (
                    ''
                  );
                }
              }
            ]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 20
                }
              },
              sorting: {
                sortModel: [
                  {
                    field: 'shipment_date',
                    sort: 'desc'
                  }
                ]
              }
            }}
            pageSizeOptions={[5, 10, 20, 25, 50, 100]}
            loading={loading}
            autoHeight
            density="compact"
            getRowHeight={() => 'auto'}
          />
        </ThemeProvider>
      </Card>
      {!loadAllYears && (
        <div className="flex flex-center m-t-16">
          <Button variant="contained" onClick={loadOldShipmentHistory}>
            Load Previous Years
          </Button>
        </div>
      )}
      {invoice && <TrackAndTraceModal open={openModal} onClose={closeModal} invoice={invoice} />}
    </Box>
  );
};

export default ShipmentHistory;
